import { useState, useEffect, useCallback } from 'react'
import {
  Button, Descriptions, Drawer, Form, Input, Modal, Radio, Select, Space, Table, Tag, Typography, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import * as uiApi from '@/api/uiAutomation'
import { useProjectStore } from '@/stores/project'
import type { UIReview } from '@/types'
import '../../styles/aurora-page.css'

const { Title, Text } = Typography

/** 复核状态: pending=待复核/confirmed=已复核 */
const reviewStatusMap: Record<string, { text: string; color: string }> = {
  pending: { text: '待复核', color: 'warning' },
  confirmed: { text: '已复核', color: 'success' },
}

/** 复核结论 */
const reviewResultMap: Record<string, { text: string; color: string }> = {
  pass: { text: '确认通过', color: 'success' },
  fail: { text: '确认失败', color: 'error' },
  false_positive: { text: '误报', color: 'purple' },
}

/** 入队原因标记（依据理由前缀区分共识分歧 / 低置信度） */
function reviewReasonTag(text: string) {
  if (text.startsWith('【共识分歧')) return <Tag color="error">共识分歧</Tag>
  if (text.startsWith('【低置信度')) return <Tag color="warning">低置信度</Tag>
  return null
}

/**
 * 断言复核（二期 SDK-04）
 * 双模型共识分歧或置信度低于阈值的断言自动进入复核队列，人工复核写入结论；
 * 页头「共识设置」维护共识开关 / 分歧策略 / 置信度阈值 / 副模型配置。
 */
export default function UIReviews() {
  const { currentProject } = useProjectStore()
  const projectId = currentProject?.id || 0

  // 列表状态
  const [reviews, setReviews] = useState<UIReview[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)
  const [statusFilter, setStatusFilter] = useState<string>('pending')

  // 复核弹窗
  const [reviewing, setReviewing] = useState<UIReview | null>(null)
  const [reviewForm] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  // AI 理由查看抽屉
  const [reasoningView, setReasoningView] = useState<UIReview | null>(null)

  const fetchReviews = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const res = await uiApi.listReviews({
        project_id: projectId, page, page_size: pageSize,
        review_status: statusFilter || undefined,
      })
      setReviews(res.data)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }, [projectId, page, pageSize, statusFilter])

  useEffect(() => {
    fetchReviews()
  }, [fetchReviews])

  const openReview = (record: UIReview) => {
    setReviewing(record)
    reviewForm.resetFields()
    reviewForm.setFieldsValue({ review_result: undefined, review_comment: '' })
  }

  const handleReviewSubmit = async () => {
    if (!reviewing) return
    try {
      const values = await reviewForm.validateFields()
      setSubmitting(true)
      await uiApi.reviewAssertion(reviewing.id, {
        review_result: values.review_result,
        review_comment: values.review_comment || '',
      })
      message.success('复核完成')
      setReviewing(null)
      fetchReviews()
    } catch (err) {
      console.error('[UIReviews] 复核提交失败:', err)
    } finally {
      setSubmitting(false)
    }
  }

  const columns: ColumnsType<UIReview> = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '用例', dataIndex: 'script_name', width: 160, ellipsis: true },
    {
      title: '断言',
      dataIndex: 'assertion',
      width: 260,
      render: (v: string, r: UIReview) => (
        <div>
          <div style={{ fontSize: 13, color: 'var(--au-slate)' }}>{v}</div>
          <div style={{ fontSize: 12, color: 'var(--au-slate-muted)', marginTop: 2 }}>步骤 #{r.step_index} ｜ 执行 #{r.execution_id}</div>
        </div>
      ),
    },
    {
      title: '置信度',
      dataIndex: 'confidence_score',
      width: 90,
      render: (v: number | null) => (v === null || v === undefined ? '-' : <Tag color={v >= 70 ? 'success' : 'warning'}>{v}</Tag>),
    },
    {
      title: '入队原因',
      key: 'reason',
      width: 110,
      render: (_, r: UIReview) => reviewReasonTag(r.ai_reasoning),
    },
    {
      title: '复核状态',
      dataIndex: 'review_status',
      width: 100,
      render: (v: string) => {
        const s = reviewStatusMap[v] || { text: v, color: 'default' }
        return <Tag color={s.color}>{s.text}</Tag>
      },
    },
    {
      title: '复核结论',
      dataIndex: 'review_result',
      width: 110,
      render: (v: string | null) => {
        if (!v) return <span style={{ fontSize: 13, color: 'var(--au-slate-muted)' }}>-</span>
        const s = reviewResultMap[v] || { text: v, color: 'default' }
        return <Tag color={s.color}>{s.text}</Tag>
      },
    },
    {
      title: '复核备注',
      dataIndex: 'review_comment',
      ellipsis: true,
      render: (v: string) => <span style={{ fontSize: 12, color: 'var(--au-slate-muted)' }}>{v || '-'}</span>,
    },
    {
      title: '入队时间',
      dataIndex: 'create_time',
      width: 160,
      render: (v: string) => (
        <span style={{ fontSize: 13, color: 'var(--au-slate-muted)' }}>
          {v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '-'}
        </span>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      // 两个胶囊按钮（AI 理由 + 复核）约 130px，加上单元格左右 22px 内边距需 180px 才不被裁切
      width: 180,
      render: (_, r: UIReview) => (
        <Space size={4}>
          <Button type="link" size="small" onClick={() => setReasoningView(r)}>AI 理由</Button>
          {r.review_status === 'pending' ? (
            <Button type="link" size="small" className="run-btn" onClick={() => openReview(r)}>复核</Button>
          ) : null}
        </Space>
      ),
    },
  ]

  return (
    <div className="aurora-page aurora-page--fit">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Title level={4} style={{ margin: 0, fontWeight: 600 }}>断言复核</Title>
          <Tag color="orange">双模型共识</Tag>
          <Tag color="blue">低置信度自动入队</Tag>
        </div>
        <Space>
          <Button onClick={fetchReviews}>刷新</Button>
        </Space>
      </div>

      <div className="search-bar" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Select
          placeholder="复核状态"
          allowClear
          style={{ width: 130 }}
          value={statusFilter || undefined}
          onChange={(v) => { setStatusFilter(v || ''); setPage(1) }}
          options={[
            { value: 'pending', label: '待复核' },
            { value: 'confirmed', label: '已复核' },
          ]}
        />
        <Button
          type="primary"
          onClick={() => setPage(1)}
          style={{ background: 'var(--au-primary)', borderColor: 'var(--au-primary)', color: '#fff' }}
        >
          查询
        </Button>
      </div>

      <div className="content-card">
        <Table
          columns={columns}
          dataSource={reviews}
          rowKey="id"
          loading={loading}
          scroll={{ y: '100%' }} /* 表体高度由 fit 布局 flex 分配，去掉 x 最小宽避免横向滚动条 */
          pagination={{
            current: page,
            total,
            pageSize,
            showSizeChanger: true,
            showTotal: (t: number) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps) },
          }}
        />
      </div>

      {/* ============ 复核操作弹窗 ============ */}
      <Modal
        title={`复核断言（步骤 #${reviewing?.step_index ?? '-'}）`}
        open={!!reviewing}
        onCancel={() => setReviewing(null)}
        onOk={handleReviewSubmit}
        confirmLoading={submitting}
        okText="提交复核"
        cancelText="取消"
        width={640}
        rootClassName="aurora-modal-wrap"
        className="aurora-modal"
      >
        {reviewing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
            <Descriptions
              column={2}
              size="small"
              labelStyle={{ fontSize: 13, color: 'var(--au-slate-muted)', width: 90 }}
            >
              <Descriptions.Item label="用例" span={2}>{reviewing.script_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="断言内容" span={2}>
                <Text strong style={{ color: 'var(--au-slate)' }}>{reviewing.assertion}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="主模型置信度">
                {reviewing.confidence_score === null || reviewing.confidence_score === undefined
                  ? '-'
                  : <Tag color={reviewing.confidence_score >= 70 ? 'success' : 'warning'}>{reviewing.confidence_score}</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="执行记录">#{reviewing.execution_id}</Descriptions.Item>
            </Descriptions>
            <div>
              <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>AI 判定理由</Text>
              <pre
                style={{
                  margin: 0,
                  maxHeight: 200,
                  overflow: 'auto',
                  borderRadius: 10,
                  padding: '10px 12px',
                  fontSize: 12,
                  lineHeight: '20px',
                  fontFamily: 'Consolas, Menlo, monospace',
                  background: '#10192b',
                  color: '#c8d6e5',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {reviewing.ai_reasoning || '（无）'}
              </pre>
            </div>
            <Form form={reviewForm} layout="vertical">
              <Form.Item name="review_result" label="复核结论" rules={[{ required: true, message: '请选择复核结论' }]}>
                <Radio.Group
                  options={[
                    { value: 'pass', label: '确认通过（断言实际满足）' },
                    { value: 'fail', label: '确认失败（断言实际不满足）' },
                    { value: 'false_positive', label: '误报（AI 误判/断言无意义）' },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
              <Form.Item name="review_comment" label="复核备注">
                <Input.TextArea rows={3} placeholder="复核说明（选填）" maxLength={2000} />
              </Form.Item>
            </Form>
          </div>
        ) : null}
      </Modal>

      {/* ============ AI 理由查看抽屉 ============ */}
      <Drawer
        title={`AI 判定理由（步骤 #${reasoningView?.step_index ?? '-'}）`}
        open={!!reasoningView}
        onClose={() => setReasoningView(null)}
        width={560}
        rootClassName="aurora-edit-drawer"
      >
        {reasoningView ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Descriptions column={1} size="small" labelStyle={{ fontSize: 13, color: 'var(--au-slate-muted)', width: 90 }}>
              <Descriptions.Item label="用例">{reasoningView.script_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="断言">{reasoningView.assertion}</Descriptions.Item>
              <Descriptions.Item label="置信度">
                {reasoningView.confidence_score === null || reasoningView.confidence_score === undefined
                  ? '-'
                  : <Tag color={reasoningView.confidence_score >= 70 ? 'success' : 'warning'}>{reasoningView.confidence_score}</Tag>}
              </Descriptions.Item>
            </Descriptions>
            <pre
              style={{
                margin: 0,
                overflow: 'auto',
                borderRadius: 10,
                padding: '12px 14px',
                fontSize: 12,
                lineHeight: '20px',
                fontFamily: 'Consolas, Menlo, monospace',
                background: '#10192b',
                color: '#c8d6e5',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {reasoningView.ai_reasoning || '（无）'}
            </pre>
          </div>
        ) : null}
      </Drawer>

    </div>
  )
}
